/**
 * The application shell: navigation, the company switcher, and the palette.
 *
 * RESPONSIVE WITHOUT SACRIFICING THE DESKTOP
 * -----------------------------------------
 * Two layouts, chosen by width rather than by a device list, because device
 * lists are always wrong eventually:
 *   - `lg` and up: a permanent sidebar. Full menu labels, always visible, the
 *     screen the shop actually uses all day.
 *   - below `lg`: no sidebar at all. A top bar with a menu button opens the same
 *     menu in a drawer, because a thirty-item menu squeezed into a phone's width
 *     is a list of truncated words.
 *
 * The desktop layout is NOT a scaled-down version of the mobile one. It is the
 * primary target, and the mobile one is the adaptation.
 *
 * The company switcher lives in the shell rather than in each screen, so no
 * screen can exist that does not show which company it belongs to. That was the
 * point of RULE 5: a screen that cannot see its own company can print an
 * invoice under the wrong GSTIN.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BarChart3,
  Boxes,
  ChevronDown,
  CircleHelp,
  LayoutDashboard,
  Menu,
  LogOut,
  Receipt,
  Search,
  Settings as SettingsIcon,
  ShoppingCart,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';
import { CommandPalette } from '@ui/components/CommandPalette';
import { Drawer } from '@ui/components/Drawer';
import { BusinessSwitcher } from '@features/documents/BusinessSwitcher';
import { isPaletteShortcut, type Command } from '@ui/lib/commandPalette';
import { NAV_GROUPS, findNavItem, isNavItemActive, type NavGroupId, type NavIcon } from './navigation';
import { cn } from '@ui/lib/cn';
import { Button } from '@ui/components/Button';
import { useAuth } from '@platform/auth/authContext';
import { HelpProvider } from '@app/help/HelpContext';
import { HelpButton } from '@app/help/HelpButton';
import { HelpDrawer } from '@app/help/HelpDrawer';
import { SyncStatusButton } from '@features/settings/SyncStatusButton';

const ICONS: Record<NavIcon, typeof LayoutDashboard> = {
  dashboard: LayoutDashboard,
  sales: Receipt,
  purchases: ShoppingCart,
  inventory: Boxes,
  contacts: Users,
  payments: Wallet,
  reports: BarChart3,
  settings: SettingsIcon,
  help: CircleHelp,
};

const GROUP_LABELS: Record<NavGroupId, string> = {
  overview: 'Overview',
  sales: 'Sales',
  purchases: 'Purchases',
  inventory: 'Inventory',
  contacts: 'Contacts',
  payments: 'Payments',
  reports: 'Reports',
  settings: 'Settings',
  help: 'Help',
};

/** The menu the shell draws. Presentational data in, links out. */
function NavList({ route, onNavigate }: { route: string; onNavigate?: () => void }) {
  const active = findNavItem(route);
  return (
    <nav aria-label="Main" className="flex flex-col gap-4 p-3" data-testid="main-nav">
      {NAV_GROUPS.map((group) => {
        const Icon = ICONS[group.icon];
        return (
          <div key={group.id}>
            <p
              data-testid={`nav-group-${group.id}`}
              className="mb-1 flex items-center gap-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500"
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
              {group.label}
            </p>
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => {
                const current = isNavItemActive(item, route);
                return (
                  <li key={item.id}>
                    <a
                      href={item.href}
                      aria-current={current ? 'page' : undefined}
                      onClick={onNavigate}
                      className={cn(
                        'flex items-center justify-between gap-2 rounded px-2 py-1.5 text-sm',
                        current
                          ? 'bg-slate-800 font-medium text-white'
                          : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-100',
                      )}
                    >
                      <span className="truncate">{item.label}</span>
                      {/* Only inside the group you are in, so the sidebar does
                          not fill with markers. And a muted one, because an
                          unbuilt screen is a plan entry, not an error. */}
                      {!item.built && active?.group === item.group ? (
                        <span className="shrink-0 text-[10px] uppercase text-slate-600">soon</span>
                      ) : null}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

export interface AppShellProps {
  readonly route: string;
  readonly children: ReactNode;
}

export function AppShell({ route, children }: AppShellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const { user, signOut } = useAuth();

  async function handleSignOut() {
    setSignOutError(null);
    setIsSigningOut(true);
    try {
      await signOut();
    } catch (caught) {
      setSignOutError(caught instanceof Error ? caught.message : 'Unable to sign out.');
    } finally {
      setIsSigningOut(false);
    }
  }

  // The menu is derived from the same model the sidebar draws, so the palette
  // can never offer a destination the menu does not have.
  const commands = useMemo<Command[]>(
    () =>
      NAV_GROUPS.flatMap((g) =>
        g.items.map((i) => ({
          id: i.id,
          label: i.label,
          group: GROUP_LABELS[i.group],
          href: i.href,
          ...(i.keywords ? { keywords: i.keywords } : {}),
        })),
      ),
    [],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isPaletteShortcut(e)) {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!profileOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !profileMenuRef.current?.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setProfileOpen(false);
      }
    };

    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [profileOpen]);

  // No effect watches `route` to close the mobile menu. `NavList` already takes
  // an `onNavigate` that closes it, and every link in the drawer goes through
  // that. Watching the route instead would be a second, redundant mechanism
  // doing the same job — and it is the kind of duplicate that survives a
  // refactor of one of the two and quietly stops working.

  return (
    <HelpProvider initialRoute={route}>
      <div className="flex min-h-screen bg-slate-950 text-slate-100">
        {/* Desktop. The primary layout, not a fallback. */}
        <aside
          data-testid="desktop-sidebar"
          className="hidden w-64 shrink-0 flex-col overflow-y-auto border-r border-slate-800 lg:flex"
        >
          <div className="flex h-14 items-center gap-2 border-b border-slate-800 px-4">
            <img src="/assets/logo.svg" alt="" className="h-8 w-8 shrink-0 rounded-full" />
            <span className="text-sm font-semibold">DNR</span>
          </div>
          <div className="border-b border-slate-800 p-3">
            <BusinessSwitcher />
          </div>
          <NavList route={route} />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 items-center gap-2 border-b border-slate-800 px-3 lg:px-5">
            <div className="flex items-center gap-2 lg:hidden">
              <button
                type="button"
                data-testid="menu-button"
                aria-label="Open menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(true)}
                className="rounded p-2 text-slate-300 hover:bg-slate-800"
              >
                <Menu className="h-5 w-5" aria-hidden />
              </button>
              <img src="/assets/logo.svg" alt="" className="h-8 w-8 shrink-0 rounded-full" />
              <span className="text-sm font-semibold">DNR</span>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <SyncStatusButton />
              <HelpButton />
              <button
                type="button"
                aria-label="Search screens"
                onClick={() => setPaletteOpen(true)}
                className="rounded p-2 text-slate-300 hover:bg-slate-800"
              >
                <Search className="h-5 w-5" aria-hidden />
              </button>
              <div ref={profileMenuRef} className="relative">
                <button
                  type="button"
                  aria-label="User profile"
                  aria-haspopup="menu"
                  aria-expanded={profileOpen}
                  onClick={() => {
                    setSignOutError(null);
                    setProfileOpen((open) => !open);
                  }}
                  className="flex max-w-64 items-center gap-2 rounded-full border border-slate-700 bg-slate-900 py-1 pl-1 pr-2 text-sm text-slate-200 hover:bg-slate-800"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-700 text-xs font-semibold text-white">
                    {user?.email?.charAt(0).toUpperCase() ?? <UserRound className="h-4 w-4" aria-hidden />}
                  </span>
                  <span className="hidden max-w-40 truncate sm:inline">{user?.email ?? 'Account'}</span>
                  <ChevronDown className="hidden h-4 w-4 shrink-0 text-slate-400 sm:block" aria-hidden />
                </button>
                {profileOpen ? (
                  <div
                    role="menu"
                    aria-label="User account"
                    className="absolute right-0 z-40 mt-2 w-64 rounded-lg border border-slate-700 bg-slate-900 p-2 shadow-xl shadow-black/30"
                  >
                    <p className="truncate px-3 py-2 text-xs text-slate-400" title={user?.email ?? undefined}>
                      {user?.email ?? 'Signed in'}
                    </p>
                    <div className="my-1 border-t border-slate-800" />
                    <Button
                      role="menuitem"
                      variant="ghost"
                      block
                      loading={isSigningOut}
                      loadingText="Signing out…"
                      icon={<LogOut className="h-4 w-4" aria-hidden />}
                      onClick={() => void handleSignOut()}
                    >
                      Sign out
                    </Button>
                    {signOutError ? <p role="alert" className="px-3 py-2 text-xs text-red-300">{signOutError}</p> : null}
                  </div>
                ) : null}
              </div>
            </div>
          </header>

          <main className="min-w-0 flex-1 overflow-auto p-4">{children}</main>
        </div>

        <Drawer open={menuOpen} title="Menu" onClose={() => setMenuOpen(false)}>
          <div className="-m-4">
            <div className="border-b border-slate-800 p-3">
              <BusinessSwitcher />
            </div>
            <NavList route={route} onNavigate={() => setMenuOpen(false)} />
          </div>
        </Drawer>

        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          commands={commands}
          onChoose={(command) => {
            if (command.href) window.location.hash = command.href;
          }}
        />

        <HelpDrawer />
      </div>
    </HelpProvider>
  );
}
